import React, { useEffect, useRef, useState } from 'react';
import { useLoaderData } from '@remix-run/react';
import type { UIMessage } from 'ai';
import { getTokenPercentage, getRoughTokenSize, MAX_TOKEN_LIMIT, formatTokenCount } from '~/utils/tokenCalculator';
import { v4 as uuidv4 } from 'uuid';
import { toast } from 'react-toastify';
import { userStore } from '~/lib/stores/user';
import { projectId as projectIdStore } from '~/lib/persistence/useChatHistory';

interface TokenLimitBannerProps {
  className?: string;
  messages: UIMessage[];
  isStreaming?: boolean;
  setMessages: any;
}

export function TokenLimitBanner({ className, messages, isStreaming = false, setMessages }: TokenLimitBannerProps) {
  const creds = useLoaderData<any>();
  const token: string | undefined = creds?.token;
  const percentage = getTokenPercentage(messages || []);
  const usedTokens = getRoughTokenSize(messages || []);
  const [expanded, setExpanded] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);
  const [contentMaxHeight, setContentMaxHeight] = useState<number>(0);
  const [summarising, setSummarising] = useState(false);
  const abortControllerRef = useRef<AbortController | null>(null);
  const isCritical = percentage >= 90;

  useEffect(() => {
    const el = contentRef.current;

    if (el) {
      const measure = () => setContentMaxHeight(el.scrollHeight);
      measure();
      window.addEventListener('resize', measure);

      return () => {
        window.removeEventListener('resize', measure);
      };
    }

    return undefined;
  }, [expanded, usedTokens]);

  if (!messages || messages.length === 0 || percentage < 75) {
    return null;
  }

  return (
    <div className={className}>
      <div
        onClick={() => setExpanded(true)}
        className={
          `w-full rounded-xl border px-4 py-2 mb-1 cursor-pointer ` +
          (isCritical
            ? 'border-red-600/40 bg-red-500/10 shadow-token-critical'
            : 'border-violet-600/40 bg-violet-500/10 shadow-token-warning')
        }
      >
        <div className="flex flex-col">
          {/* Row 1: Icon + Heading + Read more */}
          <div className="flex items-center justify-between">
            <div className="flex items-center flex-1">
              {/* <div className="i-ph:sparkle text-lg md:text-xl text-violet-300" /> */}
              <div className="flex items-center gap-2">
                <div
                  className={`text-[10px] md:text-xs font-semibold whitespace-nowrap ${isCritical ? 'text-red-100' : 'text-violet-100'}`}
                >
                  {summarising
                    ? 'Summarising, This may take a while.'
                    : `You're close to the chat's limit — ${percentage}% used`}
                </div>
                {/* <span className="px-1 pt-0.5 text-[9px] font-medium bg-blue-400/5 text-blue-300 rounded">BETA</span> */}
              </div>
            </div>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setExpanded((v) => !v);
              }}
              className={`${isCritical ? 'text-red-300 hover:text-red-200' : 'text-violet-300 hover:text-violet-200'} p-1 rounded transition-transform`}
              aria-label={expanded ? 'Collapse details' : 'Expand details'}
            >
              <div className={`i-ph:caret-down text-base transition-transform ${expanded ? 'rotate-180' : ''}`} />
            </button>
          </div>

          {/* Expanded content with smooth transition; caret stays in header */}
          <div
            ref={contentRef}
            style={{
              maxHeight: expanded ? contentMaxHeight : 0,
              opacity: expanded ? 1 : 0,
              overflow: 'hidden',
              transition: 'max-height 250ms ease, opacity 200ms ease',
            }}
          >
            <div className="pt-2">
              <div
                className={`text-[10px] md:text-[11px] space-y-1 ${isCritical ? 'text-red-200/90' : 'text-violet-200/90'}`}
              >
                <p>
                  This chat is almost full. To keep answers fast and accurate, Dualite will carry over a summrised
                  context so you don't lose your progress and continue your project.
                </p>
                <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-violet-300/90">
                  <span>
                    Project Size:{' '}
                    <span className="text-violet-100 font-medium">
                      {formatTokenCount(usedTokens)} tokens / {formatTokenCount(MAX_TOKEN_LIMIT)} tokens
                    </span>
                  </span>
                </div>
                <a
                  href="https://alphadocs.dualite.dev/prompt-techniques/chat-limit"
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`flex justify-end underline underline-offset-2 w-full ${isCritical ? 'text-red-300 hover:text-red-200' : 'text-violet-300 hover:text-violet-200'}`}
                >
                  What is the chat limit?
                </a>
              </div>
              <div className="flex items-center justify-start gap-3">
                <button
                  disabled={isStreaming || summarising}
                  onClick={async () => {
                    try {
                      setSummarising(true);
                      abortControllerRef.current = new AbortController();

                      const res = await fetch('/api/chatHistorySummariser', {
                        method: 'POST',
                        headers: {
                          'Content-Type': 'application/json',
                          'x-dualite-token': token ?? '',
                        },
                        body: JSON.stringify({
                          messages,
                          userEmail: userStore.get().email,
                          projectId: projectIdStore.get(),
                          planName: userStore.get().planName,
                        }),
                        signal: abortControllerRef.current.signal,
                      });

                      if (!res.ok) {
                        let message = 'Failed to generate summary';

                        try {
                          const err: any = await res.json();

                          if (err && typeof err.error === 'string') {
                            message = err.error;
                          }
                        } catch {
                          const txt = await res.text().catch(() => '');

                          if (txt) {
                            message = txt;
                          }
                        }
                        throw new Error(message);
                      }

                      const data = (await res.json()) as { summary: string };

                      // Expose the summary for the next chat session creation flow
                      // For now, copy to clipboard as a minimal UX and notify the user
                      // await navigator.clipboard.writeText(data.summary);
                      console.log('data.summary >>>>>> ', data.summary);

                      setMessages((messages: UIMessage[]) => [
                        ...messages,
                        {
                          role: 'user',
                          content: 'BELOW_IS_A_SUMMARY_OF_THE_CHAT_HISTORY',
                          id: uuidv4(),
                          createdAt: new Date(),
                        },
                        {
                          role: 'user',
                          content: data.summary,
                          id: uuidv4(),
                          createdAt: new Date(),
                        },
                      ]);
                      toast.success('Chat summarised. You can continue your project now.');
                    } catch (e) {
                      if (e instanceof Error && e.name === 'AbortError') {
                        toast.info('Summarisation cancelled');
                      } else {
                        console.error('Failed to generate summary: ', e);
                        toast.error(e instanceof Error ? e.message : 'Failed to generate summary');
                      }
                    } finally {
                      setSummarising(false);
                      abortControllerRef.current = null;
                    }
                  }}
                  className={`rounded-md px-4 py-2 text-xs md:text-sm font-medium transition-colors ${
                    isStreaming || summarising
                      ? isCritical
                        ? 'bg-red-600/40 text-red-100/60 cursor-not-allowed'
                        : 'bg-violet-600/40 text-violet-100/60 cursor-not-allowed'
                      : isCritical
                        ? 'text-red-100 bg-red-600/80 hover:bg-red-600'
                        : 'text-violet-100 bg-violet-600/80 hover:bg-violet-600'
                  }`}
                >
                  {summarising ? 'Summarising…' : 'Continue your project'}
                </button>
                {summarising && (
                  <button
                    type="button"
                    onClick={() => {
                      if (abortControllerRef.current) {
                        abortControllerRef.current.abort();
                      }

                      setSummarising(false);
                      abortControllerRef.current = null;
                    }}
                    className={`rounded-md px-4 py-2 text-xs md:text-sm font-medium transition-colors ${
                      isCritical
                        ? 'text-red-100 bg-red-600/60 hover:bg-red-600/80 border border-red-500/50'
                        : 'text-violet-100 bg-violet-600/60 hover:bg-violet-600/80 border border-violet-500/50'
                    }`}
                  >
                    Cancel
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
